import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { can } from '../core/access';
import { lastSeenIndicatorHtml } from '../core/format';
import { showDeviceCommentModal, showInfoModal, showRenameDeviceModal } from '../devices/device-modals';
import { commandsButtonHtml, deviceSubtitlesHtml, syncDeviceSubtitlesIn, deviceNameHtml } from '../devices/device-card';
import { showClickerRestartModal } from '../clicker/clicker-restart-modal';
import { showDuoCommandsPopover } from './duo-commands-popover';
import { openClickerLogWindow } from '../clicker/clicker-info-modal';
import { openClickerOperationsWindow } from '../clicker/clicker-operations-modal';
import { openClickerHistoryWindow } from '../clicker/clicker-history-modal';
import { canStopClicker, confirmClickerStop, stopBlockedReason } from '../clicker/clicker-shared';
import { closeShiftBtnHtml, syncCloseShiftBtn, wireCloseShiftBtn } from '../clicker/close-shift-shared';
import { isReordering } from './device-order';
import { reconcileGrid, dropOrphanedScreens } from '../devices/device-grid';
import { sortBySavedOrder, wireGridDragReorder } from './device-order';
import { connectIconSvg, disconnectIconSvg, clickerStartSvg, clickerStopSvg, clickerSettingsSvg, clickerLogSvg, clickerRegistrySvg, clickerHistorySvg, infoSvg, commentSvg, phoneGlyphSvg, adbSvg, } from '../stream/stream-controls-shared';
const DUO_POLL_MS = 20000; // server caches 30s; client refresh a bit under that
// Keep a live screen loop alive across brief absences of its device from the synced set
// (a DuoPlus API hiccup / momentary "powered off" can drop it from a single poll). Only
// tear the stream down once the device has been gone this long — so a blip no longer
// snaps the card to "disconnected"; when it returns the card re-attaches seamlessly.
const DUO_STREAM_GRACE_MS = 90000;
const duoConnected = new Map();
// The single active device-grid poll, keyed by FARM NAME (not the grid DOM element).
// renderFarmDevices() builds a fresh #device-grid element on every full re-render, so
// keying the poll's lifetime to the element made a same-farm re-render look like a
// navigation and tore every live screen loop down ("Connect → a few seconds → Not
// connected"). Keying it to the farm lets a re-render keep the loops (they re-attach to
// the rebuilt cards) while a real farm/page switch still stops them.
let duoPoll = null;
// Monochrome warning glyph for the error overlay (matches the stroked control icons —
// no emoji). Sized explicitly so it renders like the placeholder's icon slot.
const warningSvg = '<svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>';
// Stroked spinner for the "Connecting…" overlay — same house style as warningSvg, no
// emoji (the ⏳ hourglass wasn't in our icon language). Spins via the .duo-spin class.
const spinnerSvg = '<svg class="duo-spin" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M21 12a9 9 0 1 0-9 9"/></svg>';
/** Show an error message over the screen area (used when a connect/share attempt fails). */
function setDuoStatus(card, text) {
    const wrap = card.querySelector('.duo-screen-wrap');
    if (wrap)
        wrap.innerHTML = `<div class="device-player-placeholder"><span>${warningSvg}</span><strong>${esc(text)}</strong></div>`;
}
/** Connect: enable the DuoPlus browser share for this phone and embed it in the card's
 *  iframe. Lazy — fired only by the Connect button, so we never create a share (or spend
 *  the DuoPlus QPS=1 budget) for a phone nobody is watching. */
async function connectDuo(card, d, farmId) {
    if (duoConnected.has(d.udid))
        return;
    duoConnected.set(d.udid, { url: '', lastSeen: Date.now() });
    renderDuoScreen(card, d); // "Connecting…" + Disconnect button
    try {
        const r = await fetch(`/api/farms/${farmId}/duo-devices/${encodeURIComponent(d.udid)}/share`, { cache: 'no-store' });
        if (!r.ok) {
            const data = await r.json().catch(() => ({}));
            duoConnected.delete(d.udid);
            renderDuoScreen(card, d);
            setDuoStatus(card, data.error || t('farmdev.screen_unavailable', { status: r.status }));
            return;
        }
        const { url } = await r.json();
        const c = duoConnected.get(d.udid);
        if (!c)
            return; // disconnected while awaiting the share link
        c.url = url;
        renderDuoScreen(card, d); // paint the iframe
    }
    catch {
        duoConnected.delete(d.udid);
        renderDuoScreen(card, d);
        setDuoStatus(card, t('farmdev.network_error'));
    }
}
/** Disconnect: drop the embedded share (the iframe is torn out on the next render). */
function disconnectDuo(udid) {
    duoConnected.delete(udid);
}
/** Read-only info modal for a duo phone — same look as the normal device info modal
 *  (shared showInfoModal). The note/comment lives behind the separate comment button. */
function showDuoInfoModal(d) {
    const rows = [
        { label: t('farmdev.device_id'), value: d.udid },
        { label: t('farmdev.os'), value: d.os },
        { label: t('farmdev.area'), value: d.area },
        { label: t('farmdev.ip'), value: d.ip },
        { label: 'ADB', value: d.adbEnabled ? t('common.enabled') : t('common.disabled') },
        { label: t('farmdev.adb_endpoint'), value: d.adb },
    ]
        .filter((r) => !!r.value);
    const extra = can('device.rename')
        ? { label: t('common.rename'), onClick: () => showRenameDeviceModal(d.udid, d.name) }
        : undefined;
    showInfoModal(d.name, rows, extra);
}
/** Paint the screen area + connect button for the card's current connection state. */
function renderDuoScreen(card, d) {
    const wrap = card.querySelector('.duo-screen-wrap');
    const btn = card.querySelector('.duo-connect');
    if (!wrap || !btn)
        return;
    const c = duoConnected.get(d.udid);
    if (c) {
        if (c.url) {
            // Embed the DuoPlus browser share. `sandbox` allows the page's own scripts,
            // forms, popups and same-origin storage (it needs them) while still isolating
            // it from the panel. clipboard-read/write let copy/paste work inside the phone.
            wrap.innerHTML = `<iframe class="duo-screen-frame" src="${esc(c.url)}" title="${esc(d.name)}"
                allow="clipboard-read; clipboard-write"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"></iframe>`;
        }
        else {
            wrap.innerHTML = `<div class="device-player-placeholder"><span>${spinnerSvg}</span><strong>${t('farmdev.connecting')}</strong></div>`;
        }
        btn.innerHTML = disconnectIconSvg;
        btn.title = t('farmdev.disconnect');
        btn.setAttribute('aria-label', btn.title);
    }
    else {
        wrap.innerHTML = `<div class="device-player-placeholder"><span>${phoneGlyphSvg}</span><strong>${t('farmdev.not_connected')}</strong></div>`;
        btn.innerHTML = connectIconSvg;
        btn.title = t('farmdev.connect');
        btn.setAttribute('aria-label', btn.title);
    }
}
/** Reflect the clicker Start/Stop button for the card's current run-state. */
function renderDuoClicker(card, d) {
    const btn = card.querySelector('.duo-clicker');
    if (!btn)
        return;
    const running = !!d.clickerRunning;
    btn.innerHTML = running ? clickerStopSvg : clickerStartSvg;
    btn.classList.toggle('is-running', running);
    // Clicker runs over adb: disable Start until the adb endpoint is up; a running clicker
    // keeps its Stop button live.
    btn.disabled = !running && !d.adb;
    // Somebody else's run: a plain operator cannot stop it (the server refuses it too).
    const blocked = running && !canStopClicker(d.clickerStartedBy);
    if (blocked)
        btn.disabled = true;
    btn.title = blocked
        ? stopBlockedReason(d.clickerStartedBy)
        : btn.disabled ? t('farmdev.enable_adb_to_start') : (running ? t('farmdev.stop_clicker') : t('farmdev.start_clicker'));
    btn.setAttribute('aria-label', btn.title);
    syncCloseShiftBtn(card, 'duo-clicker-shift', running);
    // Keep the name subtitle (app · task) in sync with run-state. The optimistic
    // start/stop flip must repaint it too, else a stopped clicker keeps its green
    // "App · task" subtitle in the header until the 20s grid poll rebuilds the card.
    const wrap = card.querySelector('.device-name-wrap');
    if (wrap) {
        syncDeviceSubtitlesIn(wrap, d);
    }
}
/** Reflect the ADB toggle button: green when ADB is enabled, red when disabled. */
function renderDuoAdb(card, d) {
    const btn = card.querySelector('.duo-adb');
    if (!btn)
        return;
    const on = !!d.adbEnabled;
    btn.classList.toggle('adb-on', on);
    btn.classList.toggle('adb-off', !on);
    btn.title = on ? t('farmdev.adb_on_hint') : t('farmdev.adb_off_hint');
    btn.setAttribute('aria-label', btn.title);
}
function makeDuoDeviceCard(d, farmId, farmLabel) {
    const card = document.createElement('div');
    card.className = 'device-card device-card--duo online';
    card.dataset['udid'] = d.udid;
    // NOT native-draggable — reordering is pointer-event based (see farms/device-order.ts).
    const logBtn = can('device.clickerLog')
        ? `<button type="button" class="stream-control-btn duo-clicker-logs" title="${t('farmdev.clicker_logs')}" aria-label="${t('farmdev.clicker_logs')}">${clickerLogSvg}</button>`
        : '';
    const historyBtn = can('device.clickerHistory')
        ? `<button type="button" class="stream-control-btn duo-clicker-history" title="${t('farmdev.clicker_history')}" aria-label="${t('farmdev.clicker_history')}">${clickerHistorySvg}</button>`
        : '';
    const adbBtn = can('device.cloudAdb')
        ? `<button type="button" class="stream-control-btn duo-adb" title="ADB" aria-label="ADB">${adbSvg}</button>`
        : '';
    card.innerHTML = `
        <div class="device-card__header">
            <span class="status-dot status-dot--green"></span>
            <span class="device-name-wrap">
                ${deviceNameHtml(d.name, d.udid, farmLabel)}
                ${deviceSubtitlesHtml(d)}
            </span>
            ${lastSeenIndicatorHtml(d.lastSeen, 'duo-updated')}
            <div class="device-card__actions">${commandsButtonHtml()}</div>
            <button class="stream-btn duo-connect" title="${t('farmdev.connect')}" aria-label="${t('farmdev.connect')}">${connectIconSvg}</button>
        </div>
        <div class="device-card__player">
            <div class="device-stream-stage">
                <div class="device-stream-canvas-wrap duo-screen-wrap"></div>
                <div class="device-stream-controls" aria-label="${t('farmdev.device_controls')}">
                    <button type="button" class="stream-control-btn duo-info-btn" title="${t('farmdev.device_info')}" aria-label="${t('farmdev.device_info')}">${infoSvg}</button>
                    <button type="button" class="stream-control-btn duo-comment-btn" title="${t('farmdev.comment')}" aria-label="${t('farmdev.comment')}">${commentSvg}</button>
                    <span class="stream-controls__divider"></span>
                    <button type="button" class="stream-control-btn duo-clicker" title="${t('farmdev.start_clicker')}" aria-label="${t('farmdev.start_clicker')}">${clickerStartSvg}</button>
                    <button type="button" class="stream-control-btn duo-clicker-settings" title="${t('farmdev.reconfigure_clicker')}" aria-label="${t('farmdev.reconfigure_clicker')}">${clickerSettingsSvg}</button>
                    ${logBtn}
                    <button type="button" class="stream-control-btn duo-clicker-ops" title="${t('farmdev.operations_registry')}" aria-label="${t('farmdev.operations_registry')}">${clickerRegistrySvg}</button>
                    ${historyBtn}
                    ${closeShiftBtnHtml('duo-clicker-shift')}
                    ${adbBtn ? '<span class="stream-controls__divider"></span>' + adbBtn : ''}
                </div>
            </div>
        </div>
    `;
    renderDuoScreen(card, d);
    renderDuoClicker(card, d);
    renderDuoAdb(card, d);
    wireCloseShiftBtn(card, 'duo-clicker-shift', farmId, d.udid);
    // ADB enable/disable (DuoPlus openAdb/closeAdb). Enabling ADB makes the clicker usable;
    // the next 20s sync re-resolves the adb endpoint and the card repaints.
    card.querySelector('.duo-adb')?.addEventListener('click', async () => {
        const btn = card.querySelector('.duo-adb');
        if (!btn)
            return;
        const enable = !d.adbEnabled;
        if (!confirm(enable ? t('farmdev.confirm_adb_enable', { name: d.name }) : t('farmdev.confirm_adb_disable', { name: d.name })))
            return;
        btn.disabled = true;
        try {
            const res = await fetch(`/api/farms/${farmId}/duo-devices/${encodeURIComponent(d.udid)}/adb/${enable ? 'on' : 'off'}`, { method: 'POST' });
            if (res.ok) {
                d.adbEnabled = enable;
                renderDuoAdb(card, d);
                // Re-query status now (the server re-synced) so the endpoint + clicker gate
                // update immediately instead of on the next 20s poll.
                void injectDuoDevices(farmId);
            }
            else {
                const data = await res.json().catch(() => ({}));
                alert(data.error || t('farmdev.adb_toggle_failed'));
            }
        }
        catch { /* network error — leave state unchanged */ }
        finally {
            btn.disabled = false;
        }
    });
    card.querySelector('.duo-connect')?.addEventListener('click', () => {
        if (duoConnected.has(d.udid)) {
            disconnectDuo(d.udid);
            renderDuoScreen(card, d);
        }
        else
            void connectDuo(card, d, farmId);
    });
    card.querySelector('.commands-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        showDuoCommandsPopover(e.currentTarget, d.udid, d.name);
    });
    card.querySelector('.duo-info-btn')?.addEventListener('click', () => showDuoInfoModal(d));
    card.querySelector('.duo-comment-btn')?.addEventListener('click', () => {
        showDeviceCommentModal(d.udid, d.name, (d.comment || d.remark || ''));
    });
    card.querySelector('.duo-clicker')?.addEventListener('click', async () => {
        const btn = card.querySelector('.duo-clicker');
        if (!btn)
            return;
        const action = d.clickerRunning ? 'stop' : 'start';
        if (action === 'stop' && !canStopClicker(d.clickerStartedBy))
            return;
        if (action === 'stop' && !confirmClickerStop(d.name))
            return;
        btn.disabled = true;
        try {
            const res = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(d.udid)}/${action}`, { method: 'POST' });
            if (res.ok) {
                // Optimistic: flip run-state now; the 20s list poll confirms/reconciles.
                d.clickerRunning = action === 'start';
                renderDuoClicker(card, d);
            }
            else {
                const data = await res.json().catch(() => ({}));
                alert(data.error || t('farmdev.clicker_action_failed'));
            }
        }
        catch { /* network error — leave state unchanged */ }
        finally {
            btn.disabled = false;
        }
    });
    card.querySelector('.duo-clicker-settings')?.addEventListener('click', () => {
        void showClickerRestartModal(farmId, d.udid, d.name);
    });
    card.querySelector('.duo-clicker-logs')?.addEventListener('click', () => openClickerLogWindow(d.udid));
    card.querySelector('.duo-clicker-ops')?.addEventListener('click', () => openClickerOperationsWindow(farmId, d.udid, d.name));
    card.querySelector('.duo-clicker-history')?.addEventListener('click', () => openClickerHistoryWindow(farmId, d.udid, d.name));
    return card;
}
/**
 * Fetch a duo farm's synced phones. Split out of {@link injectDuoDevices} so the My
 * Devices board can reuse the same (already user-scoped) endpoint for its cloud
 * sections instead of a second payload shaped just for it.
 */
export async function fetchDuoDevices(farmId, refresh = false) {
    try {
        const r = await fetch(`/api/farms/${farmId}/duo-devices${refresh ? '?refresh=1' : ''}`);
        if (!r.ok)
            return { devices: [], failed: true };
        const body = await r.json();
        return { devices: body.devices ?? [], failed: false };
    }
    catch {
        return { devices: [], failed: true };
    }
}
/**
 * Render `data` onto `grid` as this farm's duo cards. Pure render — no fetch — so both
 * the per-farm grid and the My Devices board (which gets its cloud records from the
 * workspace payload) drive the identical reconciliation.
 */
export function injectDuoDevicesInto(grid, farmId, data) {
    // The operator's saved manual order (drag & drop, per-user localStorage) — the same
    // per-farm order the relay grid uses, so one arrangement serves both.
    const ordered = sortBySavedOrder(farmId, data, (d) => d.udid);
    reconcileGrid(grid, ordered.map((d) => ({
        udid: d.udid,
        preserve: duoConnected.has(d.udid), // live iframe — never rebuild it
        make: () => makeDuoDeviceCard(d, farmId),
    })), 
    // A phone briefly missing from one DuoPlus poll keeps its connected card; the
    // absence grace in injectDuoDevices decides when it finally goes.
    (udid) => duoConnected.has(udid));
    wireGridDragReorder(grid, farmId); // idempotent per grid element
    // A card can also disappear because its whole surface was re-rendered away; without
    // this the module map would keep issuing "connected" for a screen nobody can see.
    dropOrphanedScreens(duoConnected.keys(), disconnectDuo);
}
/**
 * Fetch a duo farm's ADB-enabled cloud phones and render them as the device grid.
 * Replaces any previously-rendered duo cards on each pass. Returns silently if the
 * grid was navigated away while awaiting.
 */
export async function injectDuoDevices(farmId, opts) {
    const { devices: data, failed } = await fetchDuoDevices(farmId, opts?.refresh === true);
    const grid = document.getElementById('device-grid');
    if (!grid || !document.body.contains(grid))
        return;
    // Age out connected screens whose phone left the active set past the grace period (a
    // brief absence is tolerated so a sync blip doesn't disconnect). Skipped on a transient
    // fetch failure, which would otherwise drop every screen at once.
    const liveUdids = new Set(data.map((d) => d.udid));
    if (!failed) {
        const now = Date.now();
        for (const [udid, c] of duoConnected) {
            if (liveUdids.has(udid))
                c.lastSeen = now;
            else if (now - c.lastSeen > DUO_STREAM_GRACE_MS)
                disconnectDuo(udid);
        }
    }
    if (isReordering())
        return;
    injectDuoDevicesInto(grid, farmId, data);
    // On a transient failure we keep any connected cards above — only show the empty/error
    // state when nothing is left on the grid.
    if (!grid.querySelector('.device-card--duo')) {
        const empty = document.createElement('p');
        empty.className = 'empty';
        empty.textContent = failed
            ? t('farmdev.duo_unreachable')
            : t('farmdev.duo_no_devices');
        grid.appendChild(empty);
    }
}
/**
 * Stop the active device-grid poll and disconnect every embedded screen. `keepFarmName`
 * is a no-op guard: when it matches the farm currently being polled the poll is left
 * running (used by renderFarmDevices so a re-render of the SAME duo farm doesn't drop
 * the connections). Called with no argument to unconditionally stop.
 */
export function stopDuoDevicePolling(keepFarmName) {
    if (!duoPoll)
        return;
    if (keepFarmName !== undefined && duoPoll.farmName === keepFarmName)
        return;
    window.clearInterval(duoPoll.timer);
    duoPoll = null;
    // Only drop screens whose card actually left the page. The My Devices board can hold
    // duo cards of this same farm, and leaving the farm grid must not reset those.
    dropOrphanedScreens(duoConnected.keys(), disconnectDuo);
}
/**
 * Poll {@link injectDuoDevices} while this duo farm's grid stays mounted. Idempotent per
 * farm: a re-render (new #device-grid element) just rebinds the timer and keeps the live
 * screen loops, which re-attach to the rebuilt cards. Switching to a different farm tears
 * the old loops down; the timer also self-stops once the grid leaves the DOM entirely.
 */
export function startDuoDevicePolling(farmId, farmName) {
    const grid = document.getElementById('device-grid');
    if (!grid)
        return;
    // Different farm than the one currently polled → drop its loops before starting.
    // Same farm (re-render) → keep them; just replace the timer bound to the new grid.
    if (duoPoll && duoPoll.farmName !== farmName)
        stopDuoDevicePolling();
    else if (duoPoll)
        window.clearInterval(duoPoll.timer);
    const timer = window.setInterval(() => {
        const g = document.getElementById('device-grid');
        // Grid gone from the DOM (navigated to a non-farm page) → stop everything.
        if (!g || !document.body.contains(g)) {
            stopDuoDevicePolling();
            return;
        }
        void injectDuoDevices(farmId);
    }, DUO_POLL_MS);
    duoPoll = { farmName, timer };
    void injectDuoDevices(farmId);
}
/** Build one duo card for a grid that mixes farms (the My Devices board), with the
 *  owning farm shown in front of the device name. Same builder as the farm grid uses. */
export function makeDuoCardForBoard(d, farmId, farmLabel) {
    return makeDuoDeviceCard(d, farmId, farmLabel);
}
/** Whether this phone currently holds a live embedded screen — the board needs it to
 *  know which cards it must not rebuild. */
export function isDuoConnected(udid) {
    return duoConnected.has(udid);
}
